/* import __COLOCATED_TEMPLATE__ from './connect.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type { ConnectionOption } from 'embercom/components/standalone/common/connect-card';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type ImportSource from 'embercom/models/import-service/import-source';
import { SourceType } from 'embercom/models/import-service/import-source';
import type SalesforcePlatform from 'embercom/objects/standalone/platforms/salesforce';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';

interface Args {
  nextStep: () => void;
  previousStep: () => void;
  sourceId?: number;
  importSource?: ImportSource;
  setImportSource?: (importSource: ImportSource) => void;
  toggleSideDrawer: () => void;
}

export default class StandaloneSalesforceArticleSyncStepConnect extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare redirectService: any;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare notificationsService: $TSFixMe;

  @tracked showManageConnection = false;
  @tracked importSource: ImportSource | undefined;
  @tracked selectedDomain: string | undefined;
  @tracked selectedConfigId: string | undefined;
  @tracked isLoadingImportSource = false;

  constructor(owner: any, args: Args) {
    super(owner, args);
    if (!this.isStandaloneApp) {
      this.showManageConnection = true;
    }
    if (this.args.importSource) {
      this.importSource = this.args.importSource;
    }
    if (this.isMultiEnvironment) {
      this.selectedConfigId = this.salesforceConfig?.isAuthFailed
        ? undefined
        : this.salesforceConfig?.id;
    }
  }

  get salesforceConfig() {
    return this.salesforcePlatform?.salesforceConfig;
  }

  get connectedConfigurations(): SalesforceConfiguration[] {
    return (this.salesforcePlatform?.salesforceConfigs ?? []).filter(
      (config) => config.isConnected,
    );
  }

  get isMultiEnvironment(): boolean {
    return this.appService.app.canUseStandaloneMultiEnv && this.connectedConfigurations.length > 0;
  }

  get selectedConfiguration(): SalesforceConfiguration | undefined {
    if (!this.selectedConfigId) {
      return undefined;
    }
    return this.connectedConfigurations.find((c) => c.id === this.selectedConfigId);
  }

  get salesforceConnectionCard(): ConnectionOption {
    return {
      value: 'salesforce',
      icon: 'salesforce',
      label: this.intl.t(
        'standalone.salesforce.setup.article-sync.step-wrapper.steps.connect.label',
      ),
      description: this.intl.t(
        'standalone.salesforce.setup.article-sync.steps.connect.description',
      ),
      isConnected: (this.salesforceConfig?.isConnected || this.importSource !== undefined) ?? false,
      isAuthFailed: this.salesforceConfig?.isAuthFailed ?? false,
      stampConnected: this.intl.t(
        'standalone.salesforce.setup.article-sync.steps.connect.stamp-connected',
        {
          environmentLabel: this.environmentLabel ?? '',
        },
      ),
      manage: () => {
        this.toggleManageConnection();
      },
      connect: () => {
        this.toggleManageConnection();
      },
    };
  }

  get continueButtonLabel() {
    if (this.isMultiEnvironment && this.selectedConfiguration) {
      return this.intl.t(
        'standalone.salesforce.setup.article-sync.steps.connect.continue-with-button-label',
        {
          environmentLabel: this.selectedConfiguration.shortDomain,
        },
      );
    }

    if (this.salesforceConfig?.isConnected) {
      return this.intl.t(
        'standalone.salesforce.setup.article-sync.steps.connect.continue-with-button-label',
        {
          environmentLabel: this.environmentLabel ?? '',
        },
      );
    }

    return this.intl.t(
      'standalone.salesforce.setup.article-sync.steps.connect.continue-button-label',
    );
  }

  get isContinueDisabled(): boolean {
    if (this.isLoadingImportSource) {
      return true;
    }
    if (this.isMultiEnvironment) {
      return !this.selectedConfigId;
    }
    return !this.importSource && (!this.salesforceConfig || this.salesforceConfig.isPendingSetup);
  }

  get isStandaloneApp() {
    return this.appService.app.isStandaloneApp;
  }

  get environmentLabel() {
    if (this.salesforceConfig?.environmentLabel !== undefined) {
      return this.salesforceConfig?.environmentLabel;
    } else if (this.selectedDomain !== undefined) {
      return this.selectedDomain;
    }
    return '';
  }

  get salesforcePlatform(): SalesforcePlatform | void {
    if (this.appService.app.isStandaloneApp) {
      return this.finStandaloneService.salesforcePlatform;
    }
  }

  @action selectConfiguration(configId: string) {
    this.selectedConfigId = configId;
    let config = this.connectedConfigurations.find((c) => c.id === configId);
    if (config && this.salesforcePlatform) {
      this.salesforcePlatform.salesforceConfig = config;
    }
  }

  @action async continueToNextStep() {
    if (!this.isMultiEnvironment) {
      this.args.nextStep();
      return;
    }

    this.isLoadingImportSource = true;
    try {
      let importSource = await this.findOrCreateImportSource();
      if (importSource) {
        this.args.setImportSource?.(importSource);
      }
      this.args.nextStep();
    } catch (error: unknown) {
      if (this.isSalesforceMetaDataError(error)) {
        this.notificationsService.notifyError(
          this.intl.t('standalone.salesforce.setup.article-sync.metadata-error'),
        );
      }
    } finally {
      this.isLoadingImportSource = false;
    }
  }

  @action startImportOnlyConnectFlow() {
    if (this.salesforcePlatform?.salesforceImportOnlyConfig) {
      this.args.nextStep();
    } else {
      let { protocol, host } = window.location;
      let uri = `${protocol}//${host}/standalone/salesforce/connect`;
      let params = `app_id_code=${this.appService.app.id}&sandbox=false&import_only=true&connect_redirect_location=article_sync`;
      this.redirectService.redirect(`${uri}?${params}`);
    }
  }

  @action toggleManageConnection() {
    if (!this.isStandaloneApp) {
      this.args.toggleSideDrawer();
    }
    this.showManageConnection = !this.showManageConnection;
  }

  @action handleImportSourceCreated(importSource: ImportSource, selectedDomain: string) {
    this.importSource = importSource;
    this.selectedDomain = selectedDomain;
    this.args.setImportSource?.(importSource);
    this.args.nextStep();
  }

  private async findOrCreateImportSource(): Promise<ImportSource | undefined> {
    let integrationId = Number(this.selectedConfigId);
    let sources = await this.knowledgeHubService.fetchImportSources();
    let existingSource = sources.find(
      (source: ImportSource) =>
        source.sourceType === SourceType.Salesforce &&
        source.context?.api_integration_id === integrationId,
    );

    if (existingSource) {
      return existingSource.reload();
    }

    let newSource = await this.knowledgeHubService.createImportSource({
      sourceType: SourceType.Salesforce,
    });
    await this.knowledgeHubService.updateImportSourceWithParam(newSource.id, {
      api_integration_id: integrationId,
    });
    return newSource.reload();
  }

  private isSalesforceMetaDataError(error: $TSFixMe): boolean {
    let payload = error?.jqXHR?.responseJSON;
    return (
      payload &&
      payload.error?.type === 'MetadataNotFoundError' &&
      payload.error?.source === 'salesforce'
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::Setup::ArticleSync::Steps::Connect': typeof StandaloneSalesforceArticleSyncStepConnect;
    'standalone/salesforce/setup/article-sync/steps/connect': typeof StandaloneSalesforceArticleSyncStepConnect;
  }
}
